
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getCategoriesApi } from '../../api/communities';
import {
  fetchCommunities,
  setSearchFilter,
  setSelectedCategory,
  clearFilters
} from '../../store/slices/communitySlice';
import './Communities.scss';
import { COURSES } from '../marketplace/data/Mockdata';
import { Button, Row, Col, Container, Form, InputGroup } from 'react-bootstrap';
import { CommunityCard } from './components/CommunityCard';

export const Communities = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    communities,
    loading,
    error,
    filters
  } = useSelector((state) => state.community);

  const [categories, setCategories] = useState([
    { _id: '', name: 'All Communities', icon: '🌐' }
  ]);
const COLOR_CLASSES = [
  'primary',
  'success',
  'warning',
  'danger',
  'info',
  'secondary',
  'dark'
];

const getRandomColor = () => {
  return COLOR_CLASSES[Math.floor(Math.random() * COLOR_CLASSES.length)];
};


  const [showMyCommunities, setShowMyCommunities] = useState(false);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    // Fetch categories from the dedicated communities API
    const fetchCategories = async () => {
      try {
        const data = await getCategoriesApi();
        if (data && data.data) {
          // Try to extract emoji from name if present
          const apiCategories = data.data.map(cat => {
            const match = cat.name.match(/(.+?)([\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])?$/u);
            return {
              _id: cat._id,
              name: match ? match[1].trim() : cat.name,
              icon: match && match[2] ? match[2] : '',
              color: getRandomColor(),
            };
          });
          setCategories([
            { _id: '', name: 'All Communities', icon: '🌐', color: 'primary' },
            ...apiCategories
          ]);
        }
      } catch (err) {
        setCategories([{ _id: '', name: 'All Communities', icon: '🌐' }]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: filters.search,
        type: filters.type,
        category: filters.selectedCategory,
      };
      dispatch(fetchCommunities(params));
    }, 400);

    return () => clearTimeout(timer);
  }, [filters.search, filters.type, filters.selectedCategory, dispatch]);

  const handleSearch = (e) => {
    dispatch(setSearchFilter(e.target.value));
  };

  const handleCategoryClick = (categoryId) => {
    dispatch(setSelectedCategory(categoryId));
  };

  const handleCommunityClick = (communityId) => {
    navigate(`/community-detail/${communityId}`);
  };

  const displayedCommunities = React.useMemo(() => {
    if (!showMyCommunities || !user) return communities;
    return communities.filter(c => {
      const ownerId = typeof c.owner === 'object' ? c.owner?._id : c.owner;
      return ownerId === user._id;
    });
  }, [communities, showMyCommunities, user]);

  return (
    <div className="communities-page">
      {/* Hero Section */}
      <section className="section-bg hero-banner">
        <Row className="flex-column-reverse flex-md-row g-0">
          <Col md={6} className="d-flex flex-column justify-content-center align-items-start">
            <div className="content-wrapper ms-auto px-3 px-xl-1 py-5">
              <div className="section-heading mb-3">
                Discover <span>communities</span>
              </div>
              <div className="description text-gray-700 mb-4">
                Our mission is to help people to find the best course online and learn with expert anytime, anywhere.
              </div>
              <Link to="/create-community">
                <Button variant="primary" className="btn-custom">Create Your Own</Button>
              </Link>
            </div>
          </Col>

          <Col md={6}>
            <div className="img-wrapper h-100">
              <img src={require('../../assets/images/communities-banner.jpg')} alt="Discover communities" className="img-fluid object-fit-cover h-100" />
            </div>
          </Col>
        </Row>
      </section>

      {/* Categories Section */}
      <section className="categories-section section-padding">
        <Container fluid="xl">
          <div className="search-box-wrapper mx-auto mb-5" style={{ maxWidth: "600px" }}>
            <InputGroup className="bg-white border rounded-pill shadow overflow-hidden px-2 py-1">
              <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700"><i className="bi bi-search"></i></InputGroup.Text>
              <Form.Control
                placeholder="Search for anythings"
                className="border-0 shadow-none px-2"
                value={filters.search}
                onChange={handleSearch}
              />
              <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                <i className="bi bi-x-circle clear-icon" onClick={() => dispatch(setSearchFilter(''))}></i>
              </InputGroup.Text>
              <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                <i className="bi bi-sliders filter-icon"></i>
              </InputGroup.Text>
            </InputGroup>
          </div>

          <div className="section-heading-wrapper">
            <div className="section-heading">Browse top <span>communities</span> category</div>
          </div>

          <Row className="g-3">
            {categories.map((cat, idx) => (
              <Col key={idx} sm={6} md={4} lg={3}>
                <div 
                  onClick={() => handleCategoryClick(cat._id)}
                  className={`category-card bg-${cat.color} bg-opacity-10 d-flex align-items-center gap-2 gap-xxl-3 h-100 hover-effect pointer p-3 ${filters.selectedCategory === cat._id ? 'border border-primary' : ''}`}>
                  <div className="icon-box bg-white d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px" }}>
                    {cat.icon}
                  </div>
                  <div className="cat-info">
                    <div className="cat-name text-base text-gray-900 fw-medium">{cat.name}</div>
                    <div className="cat-count text-xs text-gray-600">{cat.count || ''}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="browse-all-link text-gray-700 text-center mt-4 pt-2">
            We have more category & subcategory. <Link to="#" className="text-primary text-decoration-none text-nowrap">Browse All <i className="bi bi-arrow-right"></i></Link>
          </div>
        </Container>
      </section>

      {/* Best Communities Grid Section */}
      <section className="section-bg communities-grid-section section-padding">
        <Container fluid="xl">
          <div className="section-heading-wrapper d-flex justify-content-between align-items-center mb-4">
            <div className="section-heading m-0">{showMyCommunities ? 'My' : 'Best'} <span>communities</span></div>
            {user && (
              <Form.Check 
                type="switch"
                id="my-communities-switch"
                label="My Communities"
                className="fw-medium text-primary fs-5"
                checked={showMyCommunities}
                onChange={(e) => setShowMyCommunities(e.target.checked)}
              />
            )}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {displayedCommunities.length === 0 ? (
                <div className="text-center py-5 text-gray-600">
                  {showMyCommunities ? "You haven't created any communities yet." : "No communities found."}
                </div>
              ) : (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 g-xxl-4">
                  {displayedCommunities.map((community) => (
                    <div className="item-card" key={community._id}>
                      <CommunityCard community={community} />
                    </div>
                  ))}
                </div>
              )}

              {!showMyCommunities && displayedCommunities.length > 0 && (
                <div className="browse-all-link text-gray-700 text-center mt-4 pt-2">
                  We have more communities. <Link to="#" className="text-primary text-decoration-none text-nowrap">Browse All <i className="bi bi-arrow-right"></i></Link>
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </div>
  );
};
